/**
 * useWidgetURLSync — URL↔layout state synchronizer with LZ-string compression.
 *
 * Features:
 *   - Serializes grid layout into ?layout={lzHash} for shareable URLs (< 1000 chars)
 *   - Deserializes URL hash back to layout on mount, runs tessellation to fill gaps
 *   - Hydration lock (isURLHydrating) prevents circular re-render loops
 *   - History stack: replace: true during drag/resize, replace: false on tab/modal changes
 *   - Graceful fallback to default layout on corrupt/invalid hash
 *   - Fires LAYOUT_SHARE_HYDRATE audit events on serialize/deserialize
 */
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import LZString from "lz-string";
import { logURLEvent } from "@/lib/logURLEvent";
import { tessellateLayout } from "@/hooks/useGridTessellation";

const LAYOUT_PARAM = "layout";
const MAX_URL_LENGTH = 1000;

/**
 * Compress a layout array into a URI-safe LZ-string hash.
 * @param {Array} layout
 * @returns {string | null} — null if compressed output exceeds MAX_URL_LENGTH
 */
export function compressLayout(layout) {
    if (!layout || layout.length === 0) return null;
    try {
        // Strip non-essential keys to keep URL short
        const minimal = layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
        const json = JSON.stringify(minimal);
        const compressed = LZString.compressToEncodedURIComponent(json);
        if (compressed && compressed.length <= MAX_URL_LENGTH) {
            return compressed;
        }
        return null; // Too large — don't put in URL
    } catch {
        return null;
    }
}

/**
 * Decompress an LZ-string hash back into a layout array.
 * @param {string} hash
 * @returns {Array | null} — null on failure (corrupt/invalid)
 */
export function decompressLayout(hash) {
    if (!hash) return null;
    try {
        const json = LZString.decompressFromEncodedURIComponent(hash);
        if (!json) return null;
        const arr = JSON.parse(json);
        if (!Array.isArray(arr)) return null;
        // Validate minimal shape
        for (const item of arr) {
            if (typeof item.i !== "string" || typeof item.x !== "number") return null;
        }
        return arr;
    } catch {
        return null;
    }
}

/**
 * useWidgetURLSync — React hook for bi-directional URL↔layout synchronization.
 *
 * @param {Object} options
 * @param {Array} options.currentLayout - Current react-grid-layout layout items
 * @param {Function} options.onHydrateLayout - Callback to apply hydrated layout
 * @param {Set} options.hiddenWidgetIds - Currently hidden widget IDs
 * @param {string} options.viewId - Dashboard view identifier
 * @param {boolean} [options.enabled=true] - Enable/disable sync
 */
export default function useWidgetURLSync({
    currentLayout,
    onHydrateLayout,
    hiddenWidgetIds = new Set(),
    viewId = "",
    enabled = true,
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const isURLHydrating = useRef(false);
    const lastWrittenHash = useRef(null);
    const didInitialHydrate = useRef(false);
    // Use ref to avoid re-creating callbacks when searchParams changes
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    // ─── On mount: hydrate layout from URL if ?layout= exists ─────────
    useEffect(() => {
        if (!enabled || didInitialHydrate.current) return;
        didInitialHydrate.current = true;

        const hash = searchParams.get(LAYOUT_PARAM);
        if (!hash) return;

        const layoutFromURL = decompressLayout(hash);
        if (!layoutFromURL) {
            // Corrupt hash — strip it from URL, fall back to default
            const next = new URLSearchParams(searchParams);
            next.delete(LAYOUT_PARAM);
            setSearchParams(next, { replace: true });
            return;
        }

        // Apply tessellation to fill any gaps from the saved layout
        const tessellated = tessellateLayout(layoutFromURL, hiddenWidgetIds);

        isURLHydrating.current = true;
        try {
            if (onHydrateLayout) onHydrateLayout(tessellated);
        } finally {
            // Release the lock after a tick to let the state settle
            setTimeout(() => { isURLHydrating.current = false; }, 100);
        }

        logURLEvent("LAYOUT_SHARE_HYDRATE", location.pathname, {
            layout: hash.slice(0, 20) + "...",
            action: "deserialize",
            view_id: viewId,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Serialize layout to URL on significant changes ───────────────
    const writeLayoutToURL = useCallback(
        (layout, { replace = true } = {}) => {
            if (!enabled || isURLHydrating.current) return;

            const compressed = compressLayout(layout);
            if (!compressed || compressed === lastWrittenHash.current) return;
            lastWrittenHash.current = compressed;

            const next = new URLSearchParams(searchParamsRef.current);
            next.set(LAYOUT_PARAM, compressed);
            setSearchParams(next, { replace });

            logURLEvent("LAYOUT_SHARE_HYDRATE", location.pathname, {
                layout: compressed.slice(0, 20) + "...",
                action: "serialize",
                view_id: viewId,
            });
        },
        [enabled, setSearchParams, location.pathname, viewId],
    );

    // ─── Query param helpers ──────────────────────────────────────────
    const setQueryParam = useCallback(
        (key, value, { replace = false } = {}) => {
            const next = new URLSearchParams(searchParamsRef.current);
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
            setSearchParams(next, { replace });
        },
        [setSearchParams],
    );

    const removeQueryParam = useCallback(
        (key) => {
            const next = new URLSearchParams(searchParamsRef.current);
            next.delete(key);
            setSearchParams(next, { replace: true });
        },
        [setSearchParams],
    );

    return {
        isURLHydrating,
        writeLayoutToURL,
        setQueryParam,
        removeQueryParam,
        searchParams,
    };
}
